import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Leaf, Mail, Phone, MapPin } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Logo } from "./Logo";
import { SectionLabel } from "./Intro";
import { useLang } from "../../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD = "w-full rounded-xl border border-[#D1CFC8] bg-[#F6F5F0] px-4 py-3 text-[#1A1C19] placeholder-[#4A4D48]/60 focus:outline-none focus:border-[#1F5A34] focus:ring-2 focus:ring-[#1F5A34]/20 transition-all";

const EMPTY = { name: "", company: "", email: "", phone: "", province: "", category: "", volume: "", message: "" };

export const Contact = () => {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error(c.errRequired);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
      toast.success(c.toastSuccess);
      setForm(EMPTY);
    } catch (err) {
      toast.error(c.errGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-[#ECE4D6]">
      <Toaster position="top-center" richColors />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <SectionLabel>{c.label}</SectionLabel>
            <h2 className="font-display font-bold text-4xl sm:text-5xl text-[#1A1C19] leading-[1.05]">
              {c.heading}
            </h2>
            <p className="mt-6 text-[#4A4D48] text-lg leading-relaxed">{c.p}</p>
            <ul className="mt-8 space-y-2 text-[#1F5A34] font-medium">
              {c.needs.map((x) => (
                <li key={x} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {x}</li>
              ))}
            </ul>

            <div className="mt-8 pt-8 border-t border-[#D1CFC8] space-y-3 text-[#3A2718]">
              <a href="mailto:info@borealcoco.com" data-testid="contact-email" className="flex items-center gap-3 hover:text-[#1F5A34] transition-colors">
                <Mail className="w-4 h-4" /> info@borealcoco.com
              </a>
              <a href="tel:+15551234567" data-testid="contact-phone" className="flex items-center gap-3 hover:text-[#1F5A34] transition-colors">
                <Phone className="w-4 h-4" /> +1 (555) 123-4567
              </a>
              <span className="flex items-center gap-3"><MapPin className="w-4 h-4" /> {c.location}</span>
            </div>
          </div>

          <div className="md:col-span-7">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-2xl bg-[#1F5A34] p-12 text-center flex flex-col items-center justify-center h-full"
                data-testid="contact-success"
              >
                <span className="w-16 h-16 rounded-full bg-[#A9CE8A] flex items-center justify-center mb-6">
                  <Leaf className="w-8 h-8 text-[#1F5A34]" />
                </span>
                <h3 className="font-display font-bold text-3xl text-[#F6F5F0]">{c.successTitle}</h3>
                <p className="mt-3 text-[#ECE4D6]/80 max-w-sm">{c.successMsg}</p>
                <button onClick={() => setDone(false)} data-testid="contact-reset" className="mt-8 px-6 py-3 rounded-full bg-[#F6F5F0] text-[#1F5A34] font-semibold">
                  {c.sendAnother}
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl bg-[#F6F5F0] border border-[#D1CFC8] p-7 md:p-9 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="input-name" className={FIELD} placeholder={c.f_name} value={form.name} onChange={update("name")} />
                <input data-testid="input-company" className={FIELD} placeholder={c.f_company} value={form.company} onChange={update("company")} />
                <input data-testid="input-email" type="email" className={FIELD} placeholder={c.f_email} value={form.email} onChange={update("email")} />
                <input data-testid="input-phone" className={FIELD} placeholder={c.f_phone} value={form.phone} onChange={update("phone")} />
                <select data-testid="input-province" className={FIELD} value={form.province} onChange={update("province")}>
                  <option value="">{c.f_province}</option>
                  {t.provinces.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <select data-testid="input-category" className={FIELD} value={form.category} onChange={update("category")}>
                  <option value="">{c.f_category}</option>
                  {t.categories.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
                <input data-testid="input-volume" className={`${FIELD} sm:col-span-2`} placeholder={c.f_volume} value={form.volume} onChange={update("volume")} />
                <textarea data-testid="input-message" rows={4} className={`${FIELD} sm:col-span-2 resize-none`} placeholder={c.f_message} value={form.message} onChange={update("message")} />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="sm:col-span-2 inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#1F5A34] text-[#F6F5F0] font-semibold hover:bg-[#163F25] transition-colors disabled:opacity-60"
                >
                  {loading ? c.sending : c.submit}
                  {!loading && <ArrowRight className="w-4 h-4" />}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { t } = useLang();
  const c = t.footer;
  return (
    <footer className="bg-[#0E2A17] text-[#ECE4D6] py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 border-b border-white/10 pb-10">
          <div className="max-w-lg">
            <div className="mb-5"><Logo variant="white" /></div>
            <p className="text-[#ECE4D6]/70 text-sm leading-relaxed">{c.desc}</p>
            <p className="mt-4 font-display text-lg text-[#A9CE8A] font-bold">{c.tagline}</p>
            <div className="mt-5 flex flex-col gap-1.5 text-sm text-[#ECE4D6]/70">
              <a href="mailto:info@borealcoco.com" className="hover:text-white transition-colors">info@borealcoco.com</a>
              <a href="tel:+15551234567" className="hover:text-white transition-colors">+1 (555) 123-4567</a>
            </div>
          </div>
          <a href="#contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#A9CE8A] text-[#163F25] font-semibold hover:bg-white transition-colors self-start md:self-auto">
            {c.partnerCta} <ArrowRight className="w-4 h-4" />
          </a>
        </div>
        <div className="pt-8 flex flex-col md:flex-row justify-between gap-4 text-sm text-[#ECE4D6]/60">
          <span>{c.categories}</span>
          <span>© {new Date().getFullYear()} Boreal Coco · borealcoco.com</span>
        </div>
      </div>
    </footer>
  );
};
